import React, { useEffect, useCallback } from 'react'
import { useNotificationCenter } from '@/hooks/useNotificationCenter'
import {
  NotificationAction,
  NotificationActionType,
  pusherToNotification,
  pusherReadToAction,
  pusherUnreadCountToAction,
} from '@/pages/NotificationCenter/types'
import {
  NotificationData,
  NotificationReadData,
  UnreadCountData,
} from '@/services/NotificationCenter/NotificationCenterService'

/**
 * Integration hook that connects Pusher real-time events to NotificationContext
 *
 * This hook serves as a bridge between the Pusher service and the notification
 * state management, handling event conversion and dispatching appropriate actions
 * to the notification reducer.
 *
 * @param dispatch - The dispatch function from NotificationContext
 * @returns Object with connection status and control functions
 *
 * @example
 * ```typescript
 * // Inside NotificationContext
 * function NotificationProvider({ children }) {
 *   const [state, dispatch] = useReducer(notificationReducer, initialState)
 *
 *   const pusherIntegration = useNotificationPusher(dispatch)
 *
 *   const contextValue = useMemo(() => ({
 *     state: {
 *       ...state,
 *       realtime: {
 *         ...state.realtime,
 *         connected: pusherIntegration.connected,
 *       }
 *     },
 *     actions: { ... },
 *     utils: { ... }
 *   }), [state, pusherIntegration.connected])
 *
 *   return (
 *     <NotificationContext.Provider value={contextValue}>
 *       {children}
 *     </NotificationContext.Provider>
 *   )
 * }
 * ```
 */
export function useNotificationPusher(
  dispatch: React.Dispatch<NotificationAction>
) {
  const {
    service,
    isReady,
    onNotification,
    onNotificationRead,
    onUnreadCountUpdate,
  } = useNotificationCenter()

  // ================================
  // EVENT HANDLERS
  // ================================

  /**
   * Handle new notification events from Pusher
   */
  const handleNotificationCreated = useCallback(
    (data: NotificationData) => {
      try {
        const notification = pusherToNotification(data)
        dispatch({
          type: NotificationActionType.REALTIME_NOTIFICATION_RECEIVED,
          payload: { notification },
        })
      } catch (error) {
        dispatch({
          type: NotificationActionType.REALTIME_ERROR,
          payload: { error: 'Failed to process new notification' },
        })
      }
    },
    [dispatch]
  )

  /**
   * Handle notification read events from Pusher
   */
  const handleNotificationRead = useCallback(
    (data: NotificationReadData) => {
      try {
        const { notificationId, readAt } = pusherReadToAction(data)
        dispatch({
          type: NotificationActionType.REALTIME_NOTIFICATION_READ,
          payload: { notificationId, readAt },
        })
      } catch (error) {
        dispatch({
          type: NotificationActionType.REALTIME_ERROR,
          payload: { error: 'Failed to process notification read update' },
        })
      }
    },
    [dispatch]
  )

  /**
   * Handle unread count updates from Pusher
   */
  const handleUnreadCountUpdate = useCallback(
    (data: UnreadCountData) => {
      try {
        const { count } = pusherUnreadCountToAction(data)
        dispatch({
          type: NotificationActionType.UPDATE_UNREAD_COUNT,
          payload: { count },
        })
      } catch (error) {
        dispatch({
          type: NotificationActionType.REALTIME_ERROR,
          payload: { error: 'Failed to update unread count' },
        })
      }
    },
    [dispatch]
  )

  // ================================
  // CONNECTION MANAGEMENT
  // ================================

  /**
   * Set up Pusher event listeners when service is ready
   */
  useEffect(() => {
    if (!service || !isReady) {
      return undefined
    }

    // Check if Pusher is enabled for this service
    const { isPusherEnabled } = service

    // Dispatch appropriate state based on Pusher availability
    if (isPusherEnabled) {
      dispatch({
        type: NotificationActionType.REALTIME_CONNECTED,
      })
    } else {
      // In offline mode, we're "ready" but not "connected" to real-time updates
      dispatch({
        type: NotificationActionType.REALTIME_DISCONNECTED,
      })
    }

    // Set up event listeners only if Pusher is enabled
    const unsubscribers = isPusherEnabled
      ? [
          onNotification(handleNotificationCreated),
          onNotificationRead(handleNotificationRead),
          onUnreadCountUpdate(handleUnreadCountUpdate),
        ]
      : []

    // Cleanup function
    return () => {
      unsubscribers.forEach(unsub => unsub())
      dispatch({
        type: NotificationActionType.REALTIME_DISCONNECTED,
      })
    }
  }, [
    service,
    isReady,
    onNotification,
    onNotificationRead,
    onUnreadCountUpdate,
    handleNotificationCreated,
    handleNotificationRead,
    handleUnreadCountUpdate,
    dispatch,
  ])

  /**
   * Handle service connection/disconnection states
   */
  useEffect(() => {
    if (!service) {
      dispatch({
        type: NotificationActionType.REALTIME_DISCONNECTED,
      })
      return
    }

    if (!isReady) {
      dispatch({
        type: NotificationActionType.REALTIME_DISCONNECTED,
      })
    }
  }, [service, isReady, dispatch])

  // ================================
  // RETURN VALUE
  // ================================

  return {
    /** Whether Pusher service is connected and ready */
    connected: isReady,

    /** The Pusher service instance (null if not ready) */
    service,

    /** Manually reconnect if needed */
    reconnect: useCallback(() => {
      if (service) {
        // Note: The current service doesn't expose a reconnect method
        // This would need to be implemented in NotificationCenterService
        // eslint-disable-next-line no-console
        console.warn('Manual reconnection not implemented in service')
      }
    }, [service]),

    /** Disconnect from Pusher */
    disconnect: useCallback(() => {
      if (service) {
        service.disconnect()
      }
    }, [service]),
  }
}

/**
 * Default export for convenience
 */
export default useNotificationPusher
